import React, { useEffect, useState, type JSX } from "react";
import MainLayout_Component from "./MainLayout_Component";
import { Provider } from "react-redux";
import store from "./ReduxStore_Store";
import { BrowserRouter, useLocation } from "react-router";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import MaintenancePage from "./components/MaintenancePage";

interface MaintenanceModeStatus {
  enabled: boolean;
  message: string | null;
}

// Inner component that can use router hooks (must be inside BrowserRouter)
function AppContent(): JSX.Element {
  const location = useLocation();
  const [maintenanceMode, setMaintenanceMode] = useState<MaintenanceModeStatus | null>(null);
  const [maintenanceCheckComplete, setMaintenanceCheckComplete] = useState<boolean>(false);

  const isAdminRoute = location.pathname.startsWith('/admin');

  // Check maintenance mode status on mount - no authentication required
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const baseUrl = import.meta.env.VITE_DEALS_API_BASE_URL || '';
        const response = await fetch(`${baseUrl}/api/objects/settings/maintenance-mode?_t=${Date.now()}`, {
          cache: 'no-store',
        });

        if (response.ok) {
          const status: MaintenanceModeStatus = await response.json();
          console.log('🔧 [MAINTENANCE] Status:', status);
          setMaintenanceMode(status);
        } else {
          console.warn('⚠️ [MAINTENANCE] Failed to fetch maintenance mode status, assuming disabled');
          setMaintenanceMode({ enabled: false, message: null });
        }
      } catch (error) {
        console.error('❌ [MAINTENANCE] Error checking maintenance mode:', error);
        setMaintenanceMode({ enabled: false, message: null });
      } finally {
        setMaintenanceCheckComplete(true);
      }
    };

    void checkMaintenanceMode();
  }, []);

  // Show loading while checking maintenance mode (brief)
  if (!maintenanceCheckComplete) {
    return (
      <div className="h-screen flex items-center justify-center bg-cvc-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cvc-blue"></div>
      </div>
    );
  }

  // If maintenance mode is enabled and not on an admin route, show maintenance page
  // Admin routes still go through ProtectedRoute so admins can log in and disable maintenance
  if (maintenanceMode?.enabled && !isAdminRoute) {
    return <MaintenancePage message={maintenanceMode.message} />;
  }

  return (
    <ProtectedRoute>
      <MainLayout_Component />
    </ProtectedRoute>
  );
}

// End user tracking section

export default function App_Component(): JSX.Element {
  // Expose conversation/turn IDs for E2E test runner
  React.useEffect(() => {
    window.__DEALOS_TEST__ = {
      getConversationId: () => {
        try {
          const state = store.getState();
          return state.chatSlice.conversationId;
        } catch (e) {
          return null;
        }
      },
      getLastTurnId: () => {
        try {
          const state = store.getState();
          const messages = state.chatSlice.messages || [];
          const lastBotMessage = messages.filter(m => m.sender === 'chatbot' && m.turnId).pop();
          return lastBotMessage?.turnId || null;
        } catch (e) {
          return null;
        }
      },
    };
  }, []);

  return (
    <Provider store={store}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </Provider>
  );
}
